import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import cn from 'classnames';
import Icon from 'components/common/Icon';
import { NAVIGATION } from 'config/navigation.config';
import { ENV } from 'config/env';
import { usePermissions } from 'hooks/usePermissions';
import './Sidebar.css';

/**
 * Navigation sidebar.
 *
 * Single source of truth: the static NAVIGATION config (label/icon/route all
 * live there, owned by the frontend). Every item is strictly filtered by the
 * user's real `permissions`, in both mock and live-backend mode — a module
 * only shows once its `view` (or relevant) permission is actually granted.
 * The System Administrator sees everything unconditionally — `can()` already
 * returns true for that user without reading `permissions` (see
 * isAdminBypass in utils/permissions).
 */

const NavItem = ({ item, collapsed, onNavigate }) => (
  <NavLink
    to={item.path}
    className={({ isActive }) => cn('kvo-sidebar__link', { 'kvo-sidebar__link--active': isActive })}
    onClick={onNavigate}
    title={collapsed ? item.label : undefined}
  >
    <Icon name={item.icon} size={19} />
    {!collapsed && <span className="kvo-sidebar__link-label">{item.label}</span>}
  </NavLink>
);

const NavGroup = ({ item, collapsed, visibleChildren, onNavigate }) => {
  const location = useLocation();
  const childActive = visibleChildren.some((c) => location.pathname.startsWith(c.path));
  const [open, setOpen] = useState(childActive);

  if (collapsed) {
    return (
      <>
        {visibleChildren.map((child) => (
          <NavItem key={child.id} item={child} collapsed onNavigate={onNavigate} />
        ))}
      </>
    );
  }

  return (
    <div className="kvo-sidebar__group">
      <button
        type="button"
        className={cn('kvo-sidebar__link kvo-sidebar__group-toggle', { 'kvo-sidebar__link--active': childActive })}
        onClick={() => setOpen((v) => !v)}
      >
        <Icon name={item.icon} size={19} />
        <span className="kvo-sidebar__link-label">{item.label}</span>
        <Icon name="chevronDown" size={14} className={cn('kvo-sidebar__caret', { 'kvo-sidebar__caret--open': open })} />
      </button>
      {open && (
        <div className="kvo-sidebar__sub">
          {visibleChildren.map((child) => (
            <NavLink
              key={child.id}
              to={child.path}
              className={({ isActive }) => cn('kvo-sidebar__sublink', { 'kvo-sidebar__sublink--active': isActive })}
              onClick={onNavigate}
            >
              {child.label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
};

const Sidebar = ({ collapsed, mobileOpen, onCloseMobile }) => {
  const { can } = usePermissions();

  const isVisible = (item) => can(item.module, item.action);

  const visibleItems = NAVIGATION.map((item) => {
    if (item.children) {
      const visibleChildren = item.children.filter(isVisible);
      return visibleChildren.length ? { ...item, children: true, visibleChildren } : null;
    }
    return isVisible(item) ? item : null;
  }).filter(Boolean);

  return (
    <>
      <div
        className={cn('kvo-sidebar__overlay', { 'kvo-sidebar__overlay--show': mobileOpen })}
        onClick={onCloseMobile}
      />
      <aside
        className={cn('kvo-sidebar', {
          'kvo-sidebar--collapsed': collapsed,
          'kvo-sidebar--mobile-open': mobileOpen,
        })}
      >
        <div className="kvo-sidebar__brand">
          <span className="kvo-sidebar__logo">JY</span>
          {!collapsed && <span className="kvo-sidebar__brand-name">{ENV.APP_NAME}</span>}
        </div>

        <nav className="kvo-sidebar__nav">
          {visibleItems.map((item) =>
            item.children ? (
              <NavGroup
                key={item.id}
                item={item}
                collapsed={collapsed}
                visibleChildren={item.visibleChildren}
                onNavigate={onCloseMobile}
              />
            ) : (
              <NavItem key={item.id} item={item} collapsed={collapsed} onNavigate={onCloseMobile} />
            )
          )}
        </nav>

        {!collapsed && (
          <div className="kvo-sidebar__footer">
            <span className="kvo-sidebar__env">{ENV.NAME}</span>
          </div>
        )}
      </aside>
    </>
  );
};

export default Sidebar;
